import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TimezonePicker from "react-bootstrap-timezone-picker";
import "react-bootstrap-timezone-picker/dist/react-bootstrap-timezone-picker.min.css";
import { FormattedMessage, useIntl } from "react-intl";
import { useTheme } from "styled-components";
import { ConditionType, DayOfWeek } from "../../domain/orchestrationCriteria.entity";
import {
  criteriaModalMessages as messages,
  useDayOfWeekLabels,
} from "../../../orchestrator.messages";
import {
  DEFAULT_TIMEZONE,
  WindowFormValue,
  dateToWindowDate,
  dayEnd,
  dayStart,
  emptyWindow,
  endHourMinTime,
  parseWindowDate,
  startHourMaxTime,
} from "../../utils/criteriaWindow.helpers";
import { StyledDatePickerWrapper, StyledTimezoneWrapper } from "./styles";

interface WindowsInputProps {
  type: ConditionType.BOOKING_WINDOW | ConditionType.TRAVEL_WINDOW;
  windows: WindowFormValue[];
  setWindows: React.Dispatch<React.SetStateAction<WindowFormValue[]>>;
  onDayToggle: (windowId: string, day: DayOfWeek) => void;
}

const DAY_MS = 86400000;

export function WindowsInput({ type, windows, setWindows, onDayToggle }: WindowsInputProps) {
  const intl = useIntl();
  const theme = useTheme();
  const dayOfWeekLabels = useDayOfWeekLabels();
  const isBookingWindow = type === ConditionType.BOOKING_WINDOW;

  const addWindow = () => setWindows(prev => [...prev, emptyWindow()]);
  const removeWindow = (windowId: string) =>
    setWindows(prev => prev.filter(w => w.id !== windowId));

  const updateWindowDate = (
    windowId: string,
    field: "startDate" | "endDate",
    date: Date | null
  ) => {
    const value = dateToWindowDate(date);
    setWindows(prev => prev.map(w => (w.id === windowId ? { ...w, [field]: value } : w)));
  };

  return (
    <div className="space-y-4">
      <label className="block font-medium text-gray-700">
        {isBookingWindow ? (
          <FormattedMessage {...messages.bookingWindows} />
        ) : (
          <FormattedMessage {...messages.travelWindows} />
        )}
      </label>
      {windows.map((window, index) => (
        <div key={window.id} className="border rounded p-4 space-y-3 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              <FormattedMessage {...messages.windowLabel} values={{ index: index + 1 }} />
            </span>
            {windows.length > 1 && (
              <button
                type="button"
                onClick={() => removeWindow(window.id)}
                className="text-red-600 hover:text-red-800 text-sm">
                <FormattedMessage {...messages.removeWindow} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <StyledDatePickerWrapper>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FormattedMessage {...messages.startDate} />
              </label>
              <DatePicker
                className="w-full border rounded px-3 py-2 text-sm cursor-pointer"
                selected={parseWindowDate(window.startDate)}
                onChange={date => updateWindowDate(window.id, "startDate", date as Date | null)}
                selectsStart
                startDate={parseWindowDate(window.startDate)}
                endDate={parseWindowDate(window.endDate)}
                maxDate={
                  parseWindowDate(window.endDate)
                    ? new Date(parseWindowDate(window.endDate)!.getTime() - DAY_MS)
                    : undefined
                }
                minDate={new Date()}
                dateFormat="yyyy-MM-dd"
                placeholderText="yyyy-mm-dd"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </StyledDatePickerWrapper>
            <StyledDatePickerWrapper>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <FormattedMessage {...messages.endDate} />
              </label>
              <DatePicker
                className="w-full border rounded px-3 py-2 text-sm cursor-pointer"
                selected={parseWindowDate(window.endDate)}
                onChange={date => updateWindowDate(window.id, "endDate", date as Date | null)}
                selectsEnd
                startDate={parseWindowDate(window.startDate)}
                endDate={parseWindowDate(window.endDate)}
                minDate={
                  parseWindowDate(window.startDate)
                    ? new Date(parseWindowDate(window.startDate)!.getTime() + DAY_MS)
                    : new Date()
                }
                dateFormat="yyyy-MM-dd"
                placeholderText="yyyy-mm-dd"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </StyledDatePickerWrapper>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FormattedMessage {...messages.activeDays} />
            </label>
            <div className="text-gray-500 text-xs my-2">
              <FormattedMessage {...messages.noDaysSelectedHelper} />
            </div>
            <div className="flex gap-2 mb-2">
              <button
                type="button"
                onClick={() =>
                  setWindows(prev =>
                    prev.map(w =>
                      w.id === window.id ? { ...w, activeDays: Object.values(DayOfWeek) } : w
                    )
                  )
                }
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                <FormattedMessage {...messages.selectAll} />
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={() =>
                  setWindows(prev =>
                    prev.map(w => (w.id === window.id ? { ...w, activeDays: [] } : w))
                  )
                }
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline">
                <FormattedMessage {...messages.deselectAll} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {Object.values(DayOfWeek).map(day => (
                <label
                  key={day}
                  className={`px-3 py-1 rounded border cursor-pointer text-sm ${
                    window.activeDays.includes(day)
                      ? `bg-[${theme.textColor}] text-white border-[${theme.textColor}]`
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}>
                  <input
                    type="checkbox"
                    checked={window.activeDays.includes(day)}
                    onChange={() => onDayToggle(window.id, day)}
                    className="sr-only"
                  />
                  {dayOfWeekLabels[day]}
                </label>
              ))}
            </div>
          </div>
          {/* Hour range — only for BOOKING_WINDOW */}
          {isBookingWindow && (
            <div className="border-t pt-3 mt-1">
              <p className="text-sm font-medium text-gray-700 mb-0.5">
                <FormattedMessage {...messages.hourRange} />
              </p>
              <p className="text-xs text-gray-500 mb-3">
                <FormattedMessage {...messages.hourRangeDescription} />
              </p>
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    <FormattedMessage {...messages.startHour} />
                  </label>
                  <DatePicker
                    className="border rounded px-3 py-2 text-sm w-28"
                    selected={window.startHour}
                    onChange={date =>
                      setWindows(prev =>
                        prev.map(w =>
                          w.id === window.id ? { ...w, startHour: date as Date | null } : w
                        )
                      )
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    isClearable
                    placeholderText="--:--"
                    minTime={dayStart()}
                    maxTime={startHourMaxTime(window.endHour)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    <FormattedMessage {...messages.endHour} />
                  </label>
                  <DatePicker
                    className="border rounded px-3 py-2 text-sm w-28"
                    selected={window.endHour}
                    onChange={date =>
                      setWindows(prev =>
                        prev.map(w =>
                          w.id === window.id ? { ...w, endHour: date as Date | null } : w
                        )
                      )
                    }
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={30}
                    dateFormat="HH:mm"
                    timeFormat="HH:mm"
                    isClearable
                    placeholderText="--:--"
                    minTime={endHourMinTime(window.startHour)}
                    maxTime={dayEnd()}
                  />
                </div>
                <StyledTimezoneWrapper>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    <FormattedMessage {...messages.timezone} />
                  </label>
                  <TimezonePicker
                    defaultValue={window.timeZone || DEFAULT_TIMEZONE}
                    placeholder={intl.formatMessage(messages.selectTimezone)}
                    onChange={(tz: string) =>
                      setWindows(prev =>
                        prev.map(w => (w.id === window.id ? { ...w, timeZone: tz } : w))
                      )
                    }
                  />
                </StyledTimezoneWrapper>
              </div>
            </div>
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={addWindow}
        className="w-full border-2 border-dashed border-gray-300 rounded px-4 py-2 text-gray-600 hover:border-gray-400 hover:text-gray-700 text-sm">
        <FormattedMessage {...messages.addWindow} />
      </button>
    </div>
  );
}
